import { Suspense, lazy, useEffect, useState, type ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { resolvePostAuthRoute } from "@/lib/resolvePostAuthRoute";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/contexts/AuthContext";
import { WeightUnitProvider } from "@/contexts/WeightUnitContext";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { onWorkoutCompleted } from "@/lib/workoutEvents";
import { maybePromptForRating } from "@/lib/appRating";
import { TrialBootstrap } from "@/components/TrialBootstrap";
import { TrialEndedModal } from "@/components/TrialEndedModal";
import { MomentUpsell } from "@/components/monetization/MomentUpsell";
import { FriendIntentClaimer } from "@/components/FriendIntentClaimer";
import { BannedScreen } from "@/components/BannedScreen";
import { UsernameGate } from "@/components/UsernameGate";

// Lazy load all pages
// Lazy import with retry + hard reload on chunk failure.
// Dynamic imports can fail when a new deploy invalidates the previous chunk
// hashes (clients running the old index.html still reference old chunk URLs).
// Retry once, then force a one-time reload so the browser fetches fresh HTML.
const RELOAD_FLAG = "spotrai_chunk_reload_attempted";
function lazyWithRetry<T extends { default: React.ComponentType<unknown> }>(
  importer: () => Promise<T>
) {
  return lazy(async () => {
    try {
      const mod = await importer();
      // Successful load — clear the reload flag for future failures.
      try { sessionStorage.removeItem(RELOAD_FLAG); } catch { /* ignore */ }
      return mod;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const isChunkError =
        /Failed to fetch dynamically imported module/i.test(msg) ||
        /Importing a module script failed/i.test(msg) ||
        /ChunkLoadError/i.test(msg);
      if (isChunkError) {
        // Retry once in-place before reloading.
        try {
          const mod = await importer();
          try { sessionStorage.removeItem(RELOAD_FLAG); } catch { /* ignore */ }
          return mod;
        } catch {
          let alreadyReloaded = false;
          try {
            alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG) === "1";
            sessionStorage.setItem(RELOAD_FLAG, "1");
          } catch { /* ignore */ }
          if (!alreadyReloaded && typeof window !== "undefined") {
            window.location.reload();
            // Return a never-resolving promise so React doesn't surface
            // an error while the reload is happening.
            return new Promise<T>(() => {});
          }
        }
      }
      throw err;
    }
  });
}

const Auth = lazyWithRetry(() => import("./pages/Auth"));
const Onboarding = lazyWithRetry(() => import("./pages/Onboarding"));
const Home = lazyWithRetry(() => import("./pages/Home"));
const FirstWorkout = lazyWithRetry(() => import("./pages/FirstWorkout"));
const Workouts = lazyWithRetry(() => import("./pages/Workouts"));
const AIWorkout = lazyWithRetry(() => import("./pages/AIWorkout"));
const WorkoutExecution = lazyWithRetry(() => import("./pages/WorkoutExecution"));
const Tracking = lazyWithRetry(() => import("./pages/Tracking"));
const Community = lazyWithRetry(() => import("./pages/Community"));
const GroupsScreen = lazyWithRetry(() => import("./pages/GroupsScreen"));
const FriendsScreen = lazyWithRetry(() => import("./pages/FriendsScreen"));
const StreaksScreen = lazyWithRetry(() => import("./pages/StreaksScreen"));
const Compete = lazyWithRetry(() => import("./pages/Compete"));
const Profile = lazyWithRetry(() => import("./pages/Profile"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const Membership = lazyWithRetry(() => import("./pages/Membership"));
const PublicProfile = lazyWithRetry(() => import("./pages/PublicProfile"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const TeamDashboard = lazyWithRetry(() => import("./pages/TeamDashboard"));
const TeamJoin = lazyWithRetry(() => import("./pages/TeamJoin"));
const AdminCreators = lazyWithRetry(() => import("./pages/AdminCreators"));
const AdminModeration = lazyWithRetry(() => import("./pages/AdminModeration"));
const AdminAnalytics = lazyWithRetry(() => import("./pages/AdminAnalytics"));
const TrialStarted = lazyWithRetry(() => import("./pages/TrialStarted"));


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      networkMode: "offlineFirst",
      retry: (failureCount, error) => {
        // Don't retry when offline
        if (!navigator.onLine) return false;
        return failureCount < 2;
      },
      staleTime: 1000 * 60 * 5, // 5 min — serve cached data quickly
      gcTime: 1000 * 60 * 60, // 1 hour
    },
    mutations: {
      networkMode: "offlineFirst",
    },
  },
});

// Loading fallback for lazy-loaded pages
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const PushNotificationBootstrap = () => {
  const { isSupported, requestPermissionOnFirstLaunch } = usePushNotifications();

  useEffect(() => {
    if (!isSupported) return;
    void requestPermissionOnFirstLaunch();
  }, [isSupported, requestPermissionOnFirstLaunch]);

  // Ask for an App Store rating after a workout is completed (native iOS only).
  // Uses Apple's StoreKit prompt with built-in throttling — see lib/appRating.ts.
  useEffect(() => {
    return onWorkoutCompleted(() => {
      maybePromptForRating("workout-completed");
    });
  }, []);

  return null;
};

// Smart root redirect based on auth + onboarding status
const RootRedirect = () => {
  const { user, loading } = useAuth();
  const [target, setTarget] = useState<string | null>(null);

  useEffect(() => {
    if (loading || !user) {
      if (!loading && !user) setTarget("/auth");
      return;
    }

    resolvePostAuthRoute(user.id)
      .then((route) => setTarget(route))
      .catch(() => setTarget("/home"));
  }, [user, loading]);

  if (!target) return <PageLoader />;
  return <Navigate to={target} replace />;
};

// Crash fallback — keeps the shell visible so the app never goes blank.
// For dynamic-import / chunk-load errors (common after deploys invalidate
// chunk hashes), auto-reload once instead of leaving the user stuck.
// For non-chunk errors, silently retry a couple of times per mount before
// surfacing the visible fallback — first-mount errors on Profile/Settings
// are typically transient (auth-ready race, offline cache warm-up).
const CrashFallback = ({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) => {
  const msg = error?.message ?? "";
  const isChunkError =
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /ChunkLoadError/i.test(msg);

  const [retryCount, setRetryCount] = useState(0);
  const MAX_SILENT_RETRIES = 2;
  const silentRetryInFlight = retryCount < MAX_SILENT_RETRIES;

  useEffect(() => {
    if (isChunkError) {
      let reloaded = false;
      try {
        reloaded = sessionStorage.getItem("spotrai_chunk_reload_attempted") === "1";
        sessionStorage.setItem("spotrai_chunk_reload_attempted", "1");
      } catch { /* ignore */ }
      if (!reloaded) window.location.reload();
      return;
    }
    if (!silentRetryInFlight) return;
    // Backoff: 50ms, 300ms
    const delay = retryCount === 0 ? 50 : 300;
    const t = setTimeout(() => {
      setRetryCount((n) => n + 1);
      resetErrorBoundary();
    }, delay);
    return () => clearTimeout(t);
  }, [isChunkError, silentRetryInFlight, retryCount, resetErrorBoundary]);

  // While a silent retry / reload is in flight, show the neutral page loader
  // instead of an error message.
  if (isChunkError || silentRetryInFlight) {
    return <PageLoader />;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 p-6 text-center">
      <p className="text-muted-foreground text-sm">Something went wrong.</p>
      <button
        onClick={() => {
          try {
            sessionStorage.removeItem("spotrai_chunk_reload_attempted");
          } catch { /* ignore */ }
          if (isChunkError) window.location.reload();
          else {
            setRetryCount(0);
            resetErrorBoundary();
          }
        }}
        className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium"
      >
        Reload
      </button>
    </div>
  );
};

const BannedGate = ({ children }: { children: ReactNode }) => {
  const { isBanned, bannedReason, loading } = useAuth();
  if (loading) return <>{children}</>;
  if (isBanned) return <BannedScreen reason={bannedReason} />;
  return <>{children}</>;
};



const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
      <AuthProvider>
        <WeightUnitProvider>
          <TooltipProvider>
            <PushNotificationBootstrap />
            <TrialBootstrap />
            <TrialEndedModal />
            <MomentUpsell />
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <ErrorBoundary FallbackComponent={CrashFallback}>
                <FriendIntentClaimer />
                <UsernameGate />
                <BannedGate>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/" element={<RootRedirect />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/onboarding" element={<Onboarding />} />
                    <Route path="/home" element={<Home />} />
                    <Route path="/first-workout" element={<FirstWorkout />} />
                    <Route path="/workouts" element={<Workouts />} />
                    <Route path="/ai-workout" element={<AIWorkout />} />
                    <Route path="/workout-execution" element={<WorkoutExecution />} />
                    <Route path="/tracking" element={<Tracking />} />
                    <Route path="/community" element={<Community />} />
                    <Route path="/community/groups" element={<GroupsScreen />} />
                    <Route path="/community/friends" element={<FriendsScreen />} />
                    <Route path="/community/streaks" element={<StreaksScreen />} />
                    <Route path="/compete" element={<Compete />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/membership" element={<Membership />} />
                    <Route path="/u/:username" element={<PublicProfile />} />
                    <Route path="/teams/join/:code" element={<TeamJoin />} />
                    <Route path="/teams/:teamId" element={<TeamDashboard />} />
                    <Route path="/admin/creators" element={<AdminCreators />} />
                    <Route path="/admin/moderation" element={<AdminModeration />} />
                    <Route path="/admin/analytics" element={<AdminAnalytics />} />
                    <Route path="/trial-started" element={<TrialStarted />} />
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
                </BannedGate>
              </ErrorBoundary>
            </BrowserRouter>
          </TooltipProvider>
        </WeightUnitProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
